// @flow
import React from 'react';
import moment from 'moment';
import { get, first, toArray, size, toNumber } from 'lodash';
import { sort, numberCompare, createNsTranslator, handleReportPeriods } from '@helpers/helpers';
import FinancialItemCard from '../../../blocks/PatientFinancial/FinancialItemCard';
import { Loading, FormattedText, Icon, Button, Table } from '@elements';
import { ReportsMenu, ReportPage, PlatformSwitch, AppMobileHeader, EmptyState, ReportsFilters, ConditionalFeature, BlockedReport, AppMobileFooter } from '@blocks';
import type { DoctorsState, ReceiptsState, FormElementData, ReportPeriodFilter } from '@types';
import { withStorage } from '@hocs';
import { withTranslation } from 'react-i18next';
import ReportPagination from '../ReportPagination';

type Props = {
  onGetReceipts: (startDate: string, endDate: string, doctorId: number | null, periodFilter?: ReportPeriodFilter, page?: number) => void,
  defaultDoctorId: number,
  doctors: DoctorsState,
  receipts: ReceiptsState,
  componentStorage: Object,
  onItemClick: Function,
  onDownlodReport: (startDate: string, endDate: string, doctorId: number | null, periodFilter?: ReportPeriodFilter) => void,
  isManager: boolean,
  isFeatureEnabled: boolean,
  isReportBlocked: boolean,
  reportsSelectDoctor: boolean,
  t: Function,
};

type State = {
  period: string,
  doctor: string,
  startDate: string,
  endDate: string,
  year: number,
  month: number,
  showMobileFilters: boolean,
};

class ReportsReceipts extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const state = {
      period: 'today',
      doctor: this.props.defaultDoctorId || get(first(toArray(get(this.props.doctors, 'data'))), 'id'),
      startDate: moment().startOf('month').format('YYYY-MM-DD'),
      endDate: moment().endOf('month').format('YYYY-MM-DD'),
      month: moment().month() + 1,
      year: moment().year(),
      showMobileFilters: false,
      ...props.componentStorage.get('filters', {}),
    };

    this.state = {
      ...state,
      // the free date-range period was removed; fall back for filters stored before that
      period: state.period === 'custom' ? 'today' : state.period,
      month: parseInt(state.month, 10) > 0 ? state.month : moment().month() + 1,
    };
  }

  componentDidMount() {
    if (this.props.isFeatureEnabled) {
      this.handleRequest();
    }
  }

  render() {
    const {
      doctors, receipts, isManager, isFeatureEnabled, reportsSelectDoctor, isReportBlocked,
    } = this.props;
    const {
      period, doctor, year, month, showMobileFilters,
    } = this.state;

    const _ = createNsTranslator('reports', this.props.t);

    if (isReportBlocked) {
      return (
        <BlockedReport
          backLinkTo="/relatorios"
          title={_('receipts/title')}
        />
      );
    }


    const isLoading = get(receipts, 'isLoading', false);
    const isLoadingReport = get(receipts, 'isLoadingReport', false);
    // once a report is requested, downloading stays disabled until the
    // filters change (see the receipts reducer)
    const isDownloadDisabled = isLoadingReport || get(receipts, 'reportRequested', false);


    // Guard against potential null objects
    if (this.props.isFeatureEnabled && !receipts) return false;

    const receiptsData = sort(get(receipts, ['data'], {}), numberCompare('number', 'desc'));

    const paginationControls = () => (
      <ReportPagination
        meta={get(receipts, 'meta', null)}
        isLoading={isLoading}
        onPageChange={this.handlePageChange}
        itemsName={_('pagination/receipts')}
      />
    );


    const isAllEmpty = size(receiptsData) === 0;
    const renderEmptyState = () => (
      <EmptyState
        onReportFilter={this.toggleMobileFilter}
        title={_('receipts/empty_hint')}
        hint={_('receipts/empty_hint')}
      />);

    const content = !isAllEmpty && (
      <Table
        itemKey="id"
        columns={[
          {
            dataIndex: 'number',
            content: '#',
          },
          {
            dataIndex: 'patientName',
            content: _('label/patient', 'global'),
          },
          {
            dataIndex: 'doctorName',
            content: _('label/doctor', 'doctor'),
            hidden: this.state.doctor !== 'all',
          },
          {
            dataIndex: 'date',
            content: _('label/date', 'global'),
            onRenderItem: (receipt) => <FormattedText type="date" text={receipt.date} />,
          },
          {
            dataIndex: 'price',
            content: _('label/value', 'global'),
            onRenderItem: (receipt) => <FormattedText type="currency" text={receipt.price} />,
          },
          {
            dataIndex: 'id',
            content: <React.Fragment>&nbsp;</React.Fragment>,
            className: 'actionCell',
            desktopOnly: true,
            onRenderItem: () => <span className="linkAction">{_('action/view_patient', 'global')}</span>,
          },
        ]}
        fullTable
        onItemClick={this.onItemClick}
        items={receiptsData}
        mobileClickLabel={_('action/view_patient', 'global')}
        // In the app, the patient financial lists' card: the whole card opens the patient
        renderMobileItem={(receipt, { onClick }) => (
          <FinancialItemCard
            title={receipt.patientName}
            value={<FormattedText type="currency" text={receipt.price} />}
            details={`#${receipt.number}`}
            date={receipt.date}
            doctorName={this.state.doctor !== 'all' ? null : receipt.doctorName}
            onClick={onClick}
          />
        )}
      />
    );

    const filters = (
      <ReportsFilters
        reportType="receipts"
        reportsSelectDoctor={reportsSelectDoctor}
        isManager={isManager}
        doctors={doctors}
        isLoading={isLoading}
        onChangeFilter={this.handleFilterChange}
        doctor={doctor}
        period={period}
        year={year}
        month={month}
      />
    );

    return (
      <div id="reportsReceiptsPage" className="appContent reportsPage">
        <PlatformSwitch
          inject
          className="platformSwitcher"
          desktop={() => (
            <React.Fragment>
              <ReportsMenu />

              <section className="mainSection">
                <ConditionalFeature enabled={isFeatureEnabled} transparent>
                  <div className="centerContent">
                    <div className="toolbar">
                      <h1>{_('receipts/receipts')}</h1>
                      <Button disabled={isAllEmpty || isDownloadDisabled} onClick={this.handleDownloadReport}>
                        <Icon iconName="download" className="icon" size={18} />
                        <span>{_('action/download_report', 'global')}</span>
                      </Button>
                    </div>
                    <div className="desktopCard">
                      <section className="tableTrackHolder">
                        {filters}
                      </section>
                      <div className="toolbar">
                        <h2>{_('receipts/title')}</h2>
                      </div>
                      {isLoading && <Loading />}
                      {!isLoading && isAllEmpty && renderEmptyState()}
                      {!isLoading && !isAllEmpty && content}
                      {paginationControls()}
                    </div>
                  </div>
                </ConditionalFeature>
              </section>
            </React.Fragment>
          )}
          mobile={() => (
            <React.Fragment>
              <AppMobileHeader title={_('receipts/title')} backLinkTo="/relatorios">
                <div className="headerActions">
                  {!isAllEmpty && !isDownloadDisabled && (
                    <Icon iconName="download" className="icon" onClick={this.handleDownloadReport} />
                  )}
                  {(!isAllEmpty && isDownloadDisabled) && (
                    <Icon iconName="download" className="icon" />
                  )}
                  <Icon iconName="filter" className="icon" onClick={this.toggleMobileFilter} />
                  {/* <Icon iconName="refresh" className="icon" onClick={this.handleRefreshClick} /> */}
                </div>
              </AppMobileHeader>
              <ReportPage
                id="reportsReceiptsPage"
                filters={() => showMobileFilters && (
                  <ReportsFilters
                    isMobile
                    reportType="receipts"
                    onCloseMobileModal={this.toggleMobileFilter}
                    reportsSelectDoctor={reportsSelectDoctor}
                    isManager={isManager}
                    doctors={doctors}
                    isLoading={isLoading}
                    onChangeFilter={this.handleFilterChange}
                    doctor={doctor}
                    period={period}
                    year={year}
                    month={month}
                  />
                )}
                isLoading={isLoading}
                emptyState={() => isAllEmpty && renderEmptyState()}
                noPadding
              >
                <ReportPage.Table title={_('receipts/title')}>
                  {content}
                  {paginationControls()}
                </ReportPage.Table>
              </ReportPage>
              <AppMobileFooter />
            </React.Fragment>
          )}
        />
      </div>
    );
  }

  handleFilterChange = (data: FormElementData) => {
    if (data.name === 'month') {
      let m = parseInt(get(data, 'value', ''), 10);
      m = m > 0 ? m : moment().month() + 1;
      m = m <= 12 ? m : moment().month() + 1;
      this.setState({ [data.name]: m }, this.handleRequest);
    } else {
      this.setState({ [data.name]: get(data, 'value', '') }, this.handleRequest);
    }
  };

  // page defaults to 1, so every filter change resets to the first page
  handleRequest = (page: number = 1) => {
    const {
      doctor, period, startDate, endDate, month, year,
    } = this.state;

    this.props.componentStorage.set('filters', { ...this.state, showMobileFilters: false });

    const doctorFilter = doctor !== 'all' ? toNumber(doctor) : null;

    const reportPeriod = handleReportPeriods(startDate, endDate, period, month, year);


    this.props.onGetReceipts(
      reportPeriod.startDate,
      reportPeriod.endDate,
      doctorFilter,
      { period, month, year },
      page,
    );
  };

  handlePageChange = (page: number) => this.handleRequest(page);

  handleDownloadReport = () => {
    const {
      doctor, period, startDate, endDate, month, year,
    } = this.state;

    this.props.componentStorage.set('filters', { ...this.state, showMobileFilters: false });

    const doctorFilter = doctor !== 'all' ? toNumber(doctor) : null;

    const reportPeriod = handleReportPeriods(startDate, endDate, period, month, year);


    this.props.onDownlodReport(
      reportPeriod.startDate,
      reportPeriod.endDate,
      doctorFilter,
      { period, month, year },
    );
  };

  toggleMobileFilter = () => this.setState({ showMobileFilters: !this.state.showMobileFilters });

  handleRefreshClick = () => {
    this.handleRequest();
  };

  onItemClick = (item: Object) => this.props.onItemClick(item.patientId);
}

export default withTranslation()(withStorage('reportsFiltersV3', 'reportsReceipts')(ReportsReceipts));
